/**
 * Client-side throttle: instant feedback and friction on the sign-in form.
 * This is UX only — the enforceable limit lives in auth-throttle.functions.ts.
 */
export type AuthAction = "signin" | "signup" | "reset";

const STORAGE_KEY = "finendra.auth-attempts.v1";

type Record_ = { failures: number[]; lockedUntil?: number };
type Store = Partial<Record<AuthAction, Record_>>;

const WINDOW_MS = 15 * 60_000;
const LOCK_AFTER = 5;
const LOCK_MS = 15 * 60_000;

function read(): Store {
  if (typeof window === "undefined") return {};
  try {
    return JSON.parse(window.localStorage.getItem(STORAGE_KEY) ?? "{}") as Store;
  } catch {
    return {};
  }
}

function write(store: Store) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
  } catch {
    /* storage unavailable — throttle degrades to server-side only */
  }
}

function recent(entry: Record_ | undefined): number[] {
  const cutoff = Date.now() - WINDOW_MS;
  return (entry?.failures ?? []).filter((t) => t > cutoff);
}

/** Seconds the user must wait before this action is allowed again (0 = now). */
export function localCooldown(action: AuthAction): number {
  const entry = read()[action];
  if (entry?.lockedUntil && entry.lockedUntil > Date.now()) {
    return Math.ceil((entry.lockedUntil - Date.now()) / 1000);
  }
  const failures = recent(entry);
  if (failures.length < 2) return 0;

  // Progressive backoff from the 3rd attempt: 2s, 4s, 8s, 16s (capped 30s).
  const delayMs = Math.min(30_000, 2 ** (failures.length - 1) * 1000);
  const last = failures[failures.length - 1] ?? 0;
  const readyAt = last + delayMs;
  return readyAt > Date.now() ? Math.ceil((readyAt - Date.now()) / 1000) : 0;
}

export function recordLocalFailure(action: AuthAction) {
  const store = read();
  const failures = [...recent(store[action]), Date.now()];
  store[action] = {
    failures,
    ...(failures.length >= LOCK_AFTER ? { lockedUntil: Date.now() + LOCK_MS } : {}),
  };
  write(store);
}

export function clearLocalFailures(action: AuthAction) {
  const store = read();
  delete store[action];
  write(store);
}

export function formatWait(seconds: number): string {
  if (seconds < 60) return `${seconds} second${seconds === 1 ? "" : "s"}`;
  const minutes = Math.ceil(seconds / 60);
  return `${minutes} minute${minutes === 1 ? "" : "s"}`;
}
